import { useState } from "react";
import { ChevronRight, Send } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/cn";
import { composeAnswer, type Card, type Move } from "@/lib/cards";
import { TECHNIQUES } from "@/lib/chat";

/**
 * The interviewer's question, made easy to answer: tap one or several
 * options, add a few words if you like, send. A card without the note line
 * (the opening, or a legacy chip row) sends on the tap. Moves are the
 * techniques the AI thinks would help next; tapping one asks for it. The
 * card only composes a message: every write to the brief is still the AI's,
 * with Keep / Undo.
 */
export function AnswerCard({
  card,
  disabled,
  onAnswer,
  onMove,
}: {
  card: Card;
  disabled: boolean;
  onAnswer: (text: string, data: { picked: string[]; note: string | null }) => void;
  onMove: (move: Move) => void;
}) {
  const [picked, setPicked] = useState<string[]>([]);
  const [note, setNote] = useState("");
  const text = composeAnswer(picked, note);
  const submit = () => {
    if (!text || disabled) return;
    onAnswer(text, { picked, note: note.trim() || null });
    setPicked([]);
    setNote("");
  };
  const toggle = (o: string) =>
    setPicked((cur) => (card.pick === "one" ? (cur[0] === o ? [] : [o]) : cur.includes(o) ? cur.filter((x) => x !== o) : [...cur, o]));

  return (
    <div className="mb-2 space-y-2">
      {card.options.length > 0 ? (
        <div className="flex flex-wrap gap-1.5" role={card.note ? (card.pick === "one" ? "radiogroup" : "group") : undefined}>
          {card.options.map((o) =>
            card.note ? (
              <button
                key={o}
                type="button"
                role={card.pick === "one" ? "radio" : "checkbox"}
                aria-checked={picked.includes(o)}
                disabled={disabled}
                onClick={() => toggle(o)}
                className={cn(
                  "rounded-full border px-3 py-1.5 text-sm transition-colors disabled:opacity-60",
                  picked.includes(o) ? "border-primary bg-primary text-primary-foreground" : "border-border bg-secondary text-secondary-foreground hover:bg-accent",
                )}
              >
                {o}
              </button>
            ) : (
              <Button key={o} variant="secondary" size="sm" className="rounded-full" disabled={disabled} onClick={() => onAnswer(o, { picked: [o], note: null })}>
                {o}
              </Button>
            ),
          )}
        </div>
      ) : null}
      {card.note ? (
        <div className="flex items-center gap-2">
          <input
            value={note}
            disabled={disabled}
            onChange={(e) => setNote(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.nativeEvent.isComposing) {
                e.preventDefault();
                submit();
              }
            }}
            placeholder="…anything to add?"
            aria-label="Add a word"
            maxLength={500}
            className="border-input bg-background placeholder:text-muted-foreground focus-visible:outline-ring h-9 min-w-0 flex-1 rounded-full border px-3 text-sm focus-visible:outline-2"
          />
          <Button size="sm" className="rounded-full" disabled={disabled || !text} onClick={submit} aria-label="Send">
            <Send size={14} /> Send
          </Button>
        </div>
      ) : null}
      {card.moves.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {card.moves.map((m) => (
            <button
              key={m}
              type="button"
              disabled={disabled}
              onClick={() => onMove(m)}
              className="text-primary hover:bg-accent inline-flex items-center gap-0.5 rounded-full px-2.5 py-1 text-xs disabled:opacity-60"
            >
              {TECHNIQUES.find((t) => t.key === m)?.label ?? m} <ChevronRight size={12} />
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
